/* eslint-disable @typescript-eslint/no-require-imports */
import React from 'react';
import { makeObservable, observable } from 'mobx';
import {
  NamedReactParams,
  NamedTextParams,
  isLanguage,
  interpolate,
  Language,
} from 'hevy-shared/translations';
import ar from './ar.json';
import ca from './ca.json';
import en from './en.json';
import es from './es.json';
import de from './de.json';
import fr from './fr.json';
import hi from './hi.json';
import it from './it.json';
import ja from './ja.json';
import ko from './ko.json';
import pl from './pl.json';
import pt_BR from './pt_BR.json';
import pt_PT from './pt_PT.json';
import ru from './ru.json';
import tr from './tr.json';
import zh_CN from './zh_CN.json';
import zh_TW from './zh_TW.json';
import Cookies from 'js-cookie';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import 'dayjs/locale/ca';
import 'dayjs/locale/es';
import 'dayjs/locale/de';
import 'dayjs/locale/fr';
import 'dayjs/locale/hi';
import 'dayjs/locale/it';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
import 'dayjs/locale/pl';
import 'dayjs/locale/pt';
import 'dayjs/locale/pt-br';
import 'dayjs/locale/ru';
import 'dayjs/locale/tr';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';
import { detectBrowserLanguage } from './detectUserLanguage';
import { log } from 'utils/log';

/* Kept here (rather than in stores/userPreferences) so it can be imported from
the Edge runtime middleware. Importing from userPreferences pulls in Dexie
which uses BroadcastChannel and isn't supported in the Edge runtime. */
export const USER_PREF_LANG_COOKIE_KEY = 'userPreferencesLanguage';

type EN = keyof typeof en;
type Translations = Record<Language, Partial<Record<EN, string>>>;

const translations: Translations = {
  en: en,
  ar: ar,
  ca: ca,
  de: de,
  es: es,
  fr: fr,
  hi: hi,
  it: it,
  ja: ja,
  ko: ko,
  pl: pl,
  pt_BR: pt_BR,
  pt_PT: pt_PT,
  ru: ru,
  tr: tr,
  zh_CN: zh_CN,
  zh_TW: zh_TW,
};

const RTL_LANGUAGES: Language[] = ['ar'];

const setDayJsLocale = (language: Language) => {
  // dayjs expects language to be formatted like zh-cn (or just the language code)
  // but language is formatted like zh_CN
  // and pt_PT exists as just pt
  const dayJsLocale = language === 'pt_PT' ? 'pt' : language.replace('_', '-').toLowerCase();
  dayjs.locale(dayJsLocale);
};

/** Observable UI language state. Source of truth for `t()` and dayjs locale. */
class LanguageState {
  current: Language = 'en';

  constructor() {
    makeObservable(this, {
      current: observable,
    });

    if (typeof window === 'undefined') return;

    const fromCookie = Cookies.get(USER_PREF_LANG_COOKIE_KEY);
    if (fromCookie && isLanguage(fromCookie)) {
      this.current = fromCookie;
    } else {
      const detected = detectBrowserLanguage();
      if (detected) this.current = detected;
    }

    setDayJsLocale(this.current);
  }
}

export const languageState = new LanguageState();

export const setWebLanguage = (newLanguage: Language) => {
  languageState.current = newLanguage;

  setDayJsLocale(newLanguage);

  if (typeof window !== 'undefined') {
    Cookies.set(USER_PREF_LANG_COOKIE_KEY, newLanguage, { expires: 365 });
  }

  // set languageDirection, Arabic is read right-to-left
  const newDirection = RTL_LANGUAGES.includes(newLanguage) ? 'rtl' : 'ltr';

  if (typeof document !== 'undefined' && document?.documentElement.dir !== newDirection) {
    document.documentElement.dir = newDirection;
  }

  log(`Set language to ${newLanguage}`);
};

/** Called on logout — clears the cookie and resets to the detected browser language. */
export const clearWebLanguage = () => {
  if (typeof window === 'undefined') return;

  Cookies.remove(USER_PREF_LANG_COOKIE_KEY);
  const detected = detectBrowserLanguage() || 'en';
  languageState.current = detected;
  setDayJsLocale(detected);
};

export function t(id: EN, params?: undefined): string;
export function t<T extends string>(id: EN, params?: Record<T, object>): React.ReactNode;
export function t<T extends string>(id: EN, params?: NamedTextParams<T>): string;

/**
 * Handles both CSR and SSR
 * @params use formatNumber and formatDateTime on numbers and dates
 */
export function t<T extends string>(id: EN, params?: NamedTextParams<T> | NamedReactParams<T>) {
  const langTranslations = translations[languageState.current];

  const copy = langTranslations[id] || en[id];

  if (!!params) {
    return interpolate(copy, params);
  }

  return copy;
}

export const supportedLanguageOrEn = (lang: string | undefined | string[]): Language => {
  if (!lang) {
    return 'en';
  }

  const language = typeof lang === 'string' ? lang : lang[0];
  return isLanguage(language) ? language : 'en';
};
